import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type TopicRecord } from '../lib/api';

export function ScriptApprovalPage() {
  const queryClient = useQueryClient();
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [statusMsg, setStatusMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const queueQuery = useQuery({
    queryKey: ['scripts-awaiting'],
    queryFn: api.listAwaitingApproval,
    refetchInterval: 5000,
  });

  const selectedTopic = queueQuery.data?.items.find((t) => t.topic_id === selectedTopicId);
  const scriptQuery = useQuery({
    queryKey: ['script', selectedTopic?.script_id, selectedTopic?.script_version],
    queryFn: () => api.getScript(selectedTopic!.script_id!, selectedTopic!.script_version!),
    enabled: !!selectedTopic?.script_id && !!selectedTopic?.script_version,
  });

  const approveMutation = useMutation({
    mutationFn: api.approveScript,
    onSuccess: () => {
      setStatusMsg({ kind: 'ok', text: 'Approved. Pipeline advancing to voice + b-roll.' });
      setSelectedTopicId(null);
      queryClient.invalidateQueries({ queryKey: ['scripts-awaiting'] });
    },
    onError: (err: Error) => setStatusMsg({ kind: 'err', text: err.message }),
  });

  const rejectMutation = useMutation({
    mutationFn: api.rejectScript,
    onSuccess: () => {
      setStatusMsg({ kind: 'ok', text: 'Rejected. Pipeline halted.' });
      setShowRejectInput(false);
      setRejectReason('');
      setSelectedTopicId(null);
      queryClient.invalidateQueries({ queryKey: ['scripts-awaiting'] });
    },
    onError: (err: Error) => setStatusMsg({ kind: 'err', text: err.message }),
  });

  const onApprove = () => {
    if (!selectedTopic?.script_approval_task_token || !selectedTopic.script_id || !selectedTopic.script_version) return;
    approveMutation.mutate({
      task_token: selectedTopic.script_approval_task_token,
      script_id: selectedTopic.script_id,
      script_version: selectedTopic.script_version,
    });
  };

  const onReject = () => {
    if (!selectedTopic?.script_approval_task_token || !rejectReason.trim()) return;
    rejectMutation.mutate({
      task_token: selectedTopic.script_approval_task_token,
      reason: rejectReason.trim(),
    });
  };

  return (
    <div>
      <h2 className="text-3xl font-bold mb-2">Script Approval Queue</h2>
      <p className="text-parchment-700 mb-6">
        Human-in-the-loop gate #1. Review generated scripts before voice + b-roll generation.
      </p>

      {statusMsg && (
        <div
          className={`rounded-button px-4 py-2 mb-6 text-sm ${
            statusMsg.kind === 'ok'
              ? 'bg-forest-100 text-forest-800'
              : 'bg-red-100 text-red-900'
          }`}
        >
          {statusMsg.text}
        </div>
      )}

      {queueQuery.isError && (
        <p className="text-sm font-mono text-red-700 mb-4">api unreachable</p>
      )}

      {queueQuery.data && queueQuery.data.items.length === 0 && !selectedTopicId && (
        <div className="card-neumorphic text-center text-parchment-700 py-12">
          <p className="font-mono text-sm">no scripts awaiting approval</p>
        </div>
      )}

      {!selectedTopicId && queueQuery.data && queueQuery.data.items.length > 0 && (
        <ul className="space-y-2">
          {queueQuery.data.items.map((t: TopicRecord) => (
            <li
              key={t.topic_id}
              className="card-neumorphic !p-4 cursor-pointer hover:shadow-neumorphic-md transition-shadow"
              onClick={() => setSelectedTopicId(t.topic_id)}
            >
              <div className="flex justify-between items-start gap-4">
                <div>
                  <p className="font-medium">{t.topic}</p>
                  <p className="text-xs font-mono text-parchment-700 mt-1">
                    {t.length_min}min · script v{t.script_version ?? '?'}
                  </p>
                </div>
                <span className="text-xs font-mono rounded-button px-2 py-1 bg-amber-100 text-amber-900">
                  awaiting
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}

      {selectedTopic && (
        <div>
          <button
            onClick={() => {
              setSelectedTopicId(null);
              setShowRejectInput(false);
            }}
            className="text-sm font-mono text-forest-700 hover:underline mb-4"
          >
            ← back to queue
          </button>

          <div className="card-neumorphic mb-6">
            <h3 className="font-heading font-semibold mb-2">{selectedTopic.topic}</h3>
            <p className="text-xs font-mono text-parchment-700">
              {selectedTopic.length_min}min · tone: {selectedTopic.tone}
            </p>
          </div>

          {scriptQuery.isLoading && (
            <p className="text-sm font-mono text-parchment-700">loading script…</p>
          )}
          {scriptQuery.isError && (
            <p className="text-sm font-mono text-red-700">script load failed: {scriptQuery.error?.message}</p>
          )}
          {scriptQuery.data && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                <div className="lg:col-span-2 card-neumorphic">
                  <h4 className="font-mono text-xs uppercase text-parchment-700 mb-3">Script</h4>
                  <div className="space-y-4">
                    {scriptQuery.data.chapters.map((c) => (
                      <div key={c.id} className="border-l-2 border-forest-400 pl-4">
                        <p className="text-xs font-mono text-parchment-700 mb-1">
                          ~{Math.floor(c.start_estimate_sec / 60)}:
                          {String(c.start_estimate_sec % 60).padStart(2, '0')} · {c.title}
                        </p>
                        <p className="text-sm leading-relaxed">{c.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="card-neumorphic">
                  <h4 className="font-mono text-xs uppercase text-parchment-700 mb-3">B-Roll Callouts</h4>
                  <ul className="space-y-2">
                    {scriptQuery.data.b_roll_callouts.map((b) => (
                      <li key={b.id} className="text-xs">
                        <p className="font-mono text-parchment-700">
                          {Math.floor(b.timestamp_sec / 60)}:
                          {String(b.timestamp_sec % 60).padStart(2, '0')} · {b.duration_sec}s · {b.type}
                        </p>
                        <p className="mt-1">{b.visual_description}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="card-neumorphic mb-6">
                <h4 className="font-mono text-xs uppercase text-parchment-700 mb-3">Title Options</h4>
                <ul className="space-y-1">
                  {scriptQuery.data.title_options.map((title, i) => (
                    <li key={i} className="text-sm">
                      <span className="font-mono text-parchment-700">{i + 1}.</span> {title}
                    </li>
                  ))}
                </ul>
              </div>

              {!showRejectInput ? (
                <div className="flex gap-3">
                  <button
                    onClick={onApprove}
                    disabled={approveMutation.isPending}
                    className="btn-primary disabled:opacity-50"
                  >
                    {approveMutation.isPending ? 'Approving…' : 'Approve Script'}
                  </button>
                  <button
                    onClick={() => setShowRejectInput(true)}
                    className="px-4 py-2 rounded-button bg-parchment-50 text-parchment-800 shadow-neumorphic-sm hover:shadow-neumorphic-md transition-shadow"
                  >
                    Request Rewrite
                  </button>
                </div>
              ) : (
                <div className="card-neumorphic">
                  <label className="block text-sm font-medium text-parchment-800 mb-2">
                    Rewrite reason (sent back to operator log)
                  </label>
                  <textarea
                    rows={3}
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="e.g. Hook is weak, chapter 3 needs concrete data"
                    className="w-full rounded-button bg-parchment-50 px-4 py-3 shadow-neumorphic-inset font-body focus:outline-none focus:ring-2 focus:ring-forest-400"
                  />
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={onReject}
                      disabled={!rejectReason.trim() || rejectMutation.isPending}
                      className="btn-primary disabled:opacity-50 !bg-red-600 hover:!bg-red-700"
                    >
                      {rejectMutation.isPending ? 'Sending…' : 'Send Rewrite Request'}
                    </button>
                    <button
                      onClick={() => {
                        setShowRejectInput(false);
                        setRejectReason('');
                      }}
                      className="px-4 py-2 rounded-button bg-parchment-50 text-parchment-800 shadow-neumorphic-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
